import React from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Button } from 'reactstrap';
import dayjs from 'dayjs';

import Switch from '~components/form-fields/inputs/Switch';

import { updateField } from '~forms/reducers/formBuilderSlice';
import { selectSelectedField } from '~forms/selectors/formBuilder';
import { ChoiceFormField, FieldOption, FormField } from '~forms/types';

interface ComponentProps {
  readOnly: boolean;
}

const FieldProperties: React.FC<ComponentProps> = ({ readOnly }) => {
  const dispatch = useAppDispatch();
  const field = useAppSelector(selectSelectedField);

  if (!field) {
    return (
      <div className="field-properties-empty">
        <span className="field-properties-empty-icon">☰</span>
        <span>Select a field to edit its properties</span>
      </div>
    );
  }

  const handleChange = (updates: Partial<FormField>) => {
    if (readOnly) return;
    dispatch(updateField({ id: field.id, ...updates }));
  };

  const isChoice = field.type === 'multiple_choice' || field.type === 'dropdown';
  const choiceField = isChoice ? (field as ChoiceFormField) : null;
  const isRichText = field.type === 'rich_text';

  const handleAddOption = () => {
    if (!choiceField) return;
    const newOption: FieldOption = {
      id: crypto.randomUUID(),
      label: '',
      value: '',
    };
    handleChange({ options: [...choiceField.options, newOption] } as Partial<FormField>);
  };

  const handleUpdateOption = (optId: string, label: string) => {
    if (!choiceField) return;
    const updated = choiceField.options.map((o) =>
      o.id === optId ? { ...o, label, value: label } : o,
    );
    handleChange({ options: updated } as Partial<FormField>);
  };

  const handleRemoveOption = (optId: string) => {
    if (!choiceField) return;
    handleChange({ options: choiceField.options.filter((o) => o.id !== optId) } as Partial<FormField>);
  };

  // Date default: 'today'/'yesterday' keywords (resolved at fill-time) or a custom ISO date.
  const dateDefaultValue = field.default_value ?? '';
  const getDateMode = (v: string): 'none' | 'today' | 'yesterday' | 'custom' => {
    if (v === '') return 'none';
    if (v === 'today') return 'today';
    if (v === 'yesterday') return 'yesterday';
    return 'custom';
  };
  const dateDefaultMode = getDateMode(dateDefaultValue);

  const handleDateModeChange = (mode: string) => {
    if (mode === 'today' || mode === 'yesterday') {
      handleChange({ default_value: mode });
    } else if (mode === 'custom') {
      // Seed a concrete date so the picker shows a value and the mode stays "custom".
      const isCustom = dateDefaultValue && dateDefaultValue !== 'today' && dateDefaultValue !== 'yesterday';
      handleChange({ default_value: isCustom ? dateDefaultValue : dayjs().format('YYYY-MM-DD') });
    } else {
      handleChange({ default_value: '' });
    }
  };

  return (
    <div className="field-properties">
      <div className="field-properties-section">
        <div className="field-properties-section-title">Field Settings</div>

        {/* rich_text is a read-only content block — explain how the filler sees it. */}
        {isRichText && (
          <div className="field-properties-field">
            <small className="text-muted">
              Read-only content — the person filling the form sees it formatted but cannot edit it. Copy a merge
              field and paste it into the editor to insert one.
            </small>
          </div>
        )}

        {/* Label */}
        {field.type !== 'merge_field' && !isRichText && (
          <div className="field-properties-field">
            <label htmlFor="fp-label">Label</label>
            <input
              id="fp-label"
              type="text"
              value={field.label}
              disabled={readOnly}
              onChange={(e) => handleChange({ label: e.target.value })}
              placeholder="Field label"
              aria-label="Field label"
            />
          </div>
        )}

        {/* Help text */}
        {field.type !== 'section_divider' && field.type !== 'merge_field' && !isRichText && (
          <div className="field-properties-field">
            <label htmlFor="fp-help">Help Text</label>
            <textarea
              id="fp-help"
              value={field.help_text ?? ''}
              disabled={readOnly}
              onChange={(e) => handleChange({ help_text: e.target.value })}
              placeholder="Optional help text"
              aria-label="Help text"
            />
          </div>
        )}

        {/* Default / prefill value — text & paragraph */}
        {(field.type === 'single_line_text' || field.type === 'paragraph_text') && (
          <div className="field-properties-field">
            <label htmlFor="fp-default">Default value (prefill)</label>
            {field.type === 'paragraph_text' ? (
              <textarea
                id="fp-default"
                value={field.default_value ?? ''}
                disabled={readOnly}
                onChange={(e) => handleChange({ default_value: e.target.value })}
                placeholder="Prefilled text — type here, or paste a copied merge field"
                aria-label="Default value"
              />
            ) : (
              <input
                id="fp-default"
                type="text"
                value={field.default_value ?? ''}
                disabled={readOnly}
                onChange={(e) => handleChange({ default_value: e.target.value })}
                placeholder="Prefilled value"
                aria-label="Default value"
              />
            )}
            {(field.default_value ?? '').includes('{{merge:') && (
              <small className="text-muted">Includes a merge field — it will be filled in automatically.</small>
            )}
          </div>
        )}

        {/* Default value — date: Today / Yesterday / Custom */}
        {field.type === 'date' && (
          <>
            <div className="field-properties-field">
              <label htmlFor="fp-date-default">Default value</label>
              <select
                id="fp-date-default"
                className="form-select form-select-sm"
                value={dateDefaultMode}
                disabled={readOnly}
                onChange={(e) => handleDateModeChange(e.target.value)}
                aria-label="Default date"
              >
                <option value="none">No default</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="custom">Custom date…</option>
              </select>
            </div>
            {dateDefaultMode === 'custom' && (
              <div className="field-properties-field">
                <label htmlFor="fp-date-custom">Custom date</label>
                <input
                  id="fp-date-custom"
                  type="date"
                  value={field.default_value ?? ''}
                  disabled={readOnly}
                  onChange={(e) => handleChange({ default_value: e.target.value })}
                  aria-label="Custom default date"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Toggles */}
      {field.type !== 'section_divider' && field.type !== 'merge_field' && !isRichText && (
        <div className="field-properties-section">
          <div className="field-properties-section-title">Options</div>
          <Switch
            type="switch"
            id="fp-required"
            name="fp-required"
            // A read-only field can't be required — there's nothing for the user to fill in.
            value={field.required && !field.read_only}
            disabled={readOnly || field.read_only}
            onChange={() => handleChange({ required: !field.required })}
            labels={['Not Required', 'Required']}
          />
          <Switch
            type="switch"
            id="fp-readonly"
            name="fp-readonly"
            value={field.read_only}
            disabled={readOnly}
            // Turning on Read Only forces Required off.
            onChange={() =>
              handleChange(field.read_only ? { read_only: false } : { read_only: true, required: false })
            }
            labels={['Editable', 'Read Only']}
          />
          <Switch
            type="switch"
            id="fp-pdf"
            name="fp-pdf"
            value={field.include_in_pdf}
            disabled={readOnly}
            onChange={() => handleChange({ include_in_pdf: !field.include_in_pdf })}
            labels={['Exclude from PDF', 'Include in PDF']}
          />
        </div>
      )}

      {/* Rich-text blocks are always read-only; the only relevant toggle is PDF inclusion (e.g. T&Cs). */}
      {isRichText && (
        <div className="field-properties-section">
          <div className="field-properties-section-title">Options</div>
          <Switch
            type="switch"
            id="fp-pdf"
            name="fp-pdf"
            value={field.include_in_pdf}
            disabled={readOnly}
            onChange={() => handleChange({ include_in_pdf: !field.include_in_pdf })}
            labels={['Exclude from PDF', 'Include in PDF']}
          />
        </div>
      )}

      {/* Options editor for choice types */}
      {isChoice && choiceField && (
        <div className="field-properties-section">
          <div className="field-properties-section-title">Options</div>
          <div className="field-properties-options">
            {choiceField.options.map((opt) => (
              <div key={opt.id} className="field-option-row">
                <input
                  type="text"
                  value={opt.label}
                  disabled={readOnly}
                  onChange={(e) => handleUpdateOption(opt.id, e.target.value)}
                  placeholder="Option label"
                  aria-label="Option label"
                />
                {!readOnly && (
                  <Button
                    size="sm"
                    color="link"
                    className="p-0 text-danger"
                    onClick={() => handleRemoveOption(opt.id)}
                    aria-label="Remove option"
                  >
                    ✕
                  </Button>
                )}
              </div>
            ))}
            {!readOnly && (
              <Button size="sm" color="link" className="p-0" onClick={handleAddOption}>
                + Add option
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Version note */}
      <div className="field-properties-section">
        <div className="field-properties-version-note">
          Changes are saved automatically to the current draft version. Publish the form to lock this version and
          create the next draft.
        </div>
      </div>
    </div>
  );
};

export default FieldProperties;
